import {
  AgentOverview,
  AdherenceEvents,
  AgentEvents,
  RealTimeAdherenceChangedSubscription,
  RealTimeAdherenceEventsChangedSubscription,
  RealTimeAgentEventsChangedSubscription,
  RealTimeScheduleChangedSubscription,
  ScheduleChange,
  IdOrEmptyResponse,
  IntradayMeasurementsChangedSubscription,
} from '../../graphql/components';
import { SubscriptionDefinition } from './types';

export const webSocketStateOperationName = 'onWebSocketState';

/**
 * Mandatory definition for CustomSubscriptionClient, it provides convenient way to receive events about transport
 * protocol (ws channel, STOMP subscriptions)...
 * When WS is disconnected or any other error occurs, basic subscriptions will not change their state but error
 * will be propagated to `useWebSocketStateSubscription` hook as its data.
 */
export const createWebSocketStateSubscriptionDefinition = (): SubscriptionDefinition<any, any> => {
  const channel = 'client/onWebSocketState';

  return {
    isClientOnly: true,
    operationName: webSocketStateOperationName,
    channel,
    transformFn: (data) => data, // noop
    createDynamicChannelName: () => channel,
  };
};

const withAgentId = (variables: Record<string, any>, channel: string) => {
  if (!variables.agentId) {
    return channel;
  }

  return `${channel}_${variables.agentId}`;
};

export const createRealTimeScheduleChangedSubscriptionDefinition = (
  tenant: string
): SubscriptionDefinition<ScheduleChange, RealTimeScheduleChangedSubscription> => {
  const operationName = 'RealTimeScheduleChanged';
  const channel = `/topic/${tenant}_schedule-changed`; // all agents

  return {
    operationName,
    channel,
    transformFn: (data) => ({
      realTimeScheduleChanged: {
        ...data,
        __typename: 'ScheduleChange',
      },
    }),
    createDynamicChannelName: (variables) => withAgentId(variables, channel),
  };
};

export const createRealTimeAgentEventsChangedSubscriptionDefinition = (
  tenant: string
): SubscriptionDefinition<AgentEvents, RealTimeAgentEventsChangedSubscription> => {
  const operationName = 'RealTimeAgentEventsChanged';
  const channel = `/topic/${tenant}_agent-events-changed`;

  return {
    operationName,
    channel,
    transformFn: (data) => ({
      realTimeAgentEventsChanged: {
        __typename: 'AgentEvents',
        agentEvents: data.agentEvents.map((event) => ({
          ...event,
          __typename: 'AgentEvent',
          agent: { ...event.agent, __typename: 'AgentInfo' },
        })),
      },
    }),

    createDynamicChannelName: (variables) => withAgentId(variables, channel),
  };
};

export const createRealTimeAdherenceChangedSubscriptionDefinition = (
  tenant: string
): SubscriptionDefinition<AgentOverview, RealTimeAdherenceChangedSubscription> => {
  const operationName = 'RealTimeAdherenceChanged';
  const channel = `/topic/${tenant}_adherence-changed`;

  return {
    operationName,
    channel,
    transformFn: (data) => ({
      realTimeAdherenceChanged: {
        __typename: 'AgentOverview',
        ...data,
        agent: {
          __typename: 'AgentInfo',
          ...data.agent,
        },
      },
    }),
    createDynamicChannelName: (variables) => withAgentId(variables, channel),
  };
};

export const createRealTimeAdherenceEventsChangedSubscriptionDefinition = (
  tenant: string
): SubscriptionDefinition<AdherenceEvents, RealTimeAdherenceEventsChangedSubscription> => {
  const operationName = 'RealTimeAdherenceEventsChanged';
  const channel = `/topic/${tenant}_adherence-events-changed`;

  return {
    operationName,
    channel,
    transformFn: (data) => ({
      realTimeAdherenceEventsChanged: {
        __typename: 'AdherenceEvents',
        adherenceEvents: data.adherenceEvents.map((event) => ({
          ...event,
          __typename: 'AdherenceEvent',
          agent: { ...event.agent, __typename: 'AgentInfo' },
        })),
      },
    }),
    createDynamicChannelName: (variables) => withAgentId(variables, channel),
  };
};

export const createIntradayMeasurementsChangedSubscriptionDefinition = (
  tenant: string
): SubscriptionDefinition<IdOrEmptyResponse, IntradayMeasurementsChangedSubscription> => {
  const operationName = 'IntradayMeasurementsChanged';
  const channel = `/topic/${tenant}_intraday-measurements-changed`;

  return {
    operationName,
    channel,
    transformFn: () => ({
      intradayMeasurementsChanged: {
        // Message does not contain any meaningfull data, but GraphQL still needs at least something, so `IdOrEmptyResponse` is used
        id: '',
      },
    }),
    createDynamicChannelName: () => channel,
  };
};

export const WEBSOCKET_DEFAULTS = {
  reconnectDelay: 5000, // time after which to reconnect in ms
  heartbeatIncoming: 20000, // incoming heartbeat time in ms (server => client)
  heartbeatOutgoing: 20000, // outgoing heartbeat time in ms (client => server)
} as const;
